import { useState, type FormEvent } from "react";
import { categoryOptions, evidenceOptions } from "@/mocks/submit";

const FORM_ENDPOINT = "https://readdy.ai/api/form/dah7vejp14a1h3mr6eo0";
const MAX_LENGTH = 500;

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function SubmitForm() {
  const [contentLength, setContentLength] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("投稿已收到，我们会按保密机制处理。");
      form.reset();
      setContentLength(0);
      return;
    }

    const content = String(formData.get("content") ?? "");
    if (content.length > MAX_LENGTH) {
      setStatus("error");
      setMessage(`投稿正文请控制在 ${MAX_LENGTH} 字以内，当前为 ${content.length} 字。`);
      return;
    }

    if (content.trim().length < 20) {
      setStatus("error");
      setMessage("正文内容太短了，请至少描述清楚一件事，不少于 20 字。");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") {
        return;
      }
      const text = String(value).trim();
      if (text) {
        payload.append(key, text);
      }
    });

    setSubmitting(true);
    setStatus("idle");
    setMessage("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ?? parsed?.message ?? parsed?.meta?.detail ?? responseText ?? "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK";

      if (isOk && !isSpam) {
        setStatus("success");
        setMessage("投稿已提交。我们会按保密机制处理，感谢你愿意说出来。");
        form.reset();
        setContentLength(0);
      } else {
        setStatus("error");
        setMessage(
          isSpam
            ? "提交未通过校验，请调整内容后重试。"
            : serverMsg || "提交失败，请稍后再试，或稍后重新填写。",
        );
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，投稿尚未提交成功，请检查网络后重试。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      data-readdy-form
      id="anonymous-submit-form"
      onSubmit={handleSubmit}
      action={FORM_ENDPOINT}
      method="post"
      className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-8"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-1">
          <label htmlFor="category" className="block text-sm font-medium text-foreground-800 mb-2">
            信息类型 <span className="text-accent-600">*</span>
          </label>
          <select
            id="category"
            name="category"
            required
            defaultValue=""
            className="w-full px-4 py-3 rounded-md text-sm bg-background-100 border border-background-300 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
          >
            <option value="" disabled>
              请选择最接近的类型
            </option>
            {categoryOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="md:col-span-1">
          <label htmlFor="region" className="block text-sm font-medium text-foreground-800 mb-2">
            涉及地区（选填）
          </label>
          <input
            id="region"
            name="region"
            type="text"
            placeholder="例如：华东某市，不必精确到街道"
            className="w-full px-4 py-3 rounded-md text-sm bg-background-100 border border-background-300 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
          />
        </div>

        <div className="md:col-span-2">
          <label htmlFor="title" className="block text-sm font-medium text-foreground-800 mb-2">
            一句话概括 <span className="text-accent-600">*</span>
          </label>
          <input
            id="title"
            name="title"
            type="text"
            required
            maxLength={80}
            placeholder="用一句话说明问题，例如：某生产线长期存在无防护作业"
            className="w-full px-4 py-3 rounded-md text-sm bg-background-100 border border-background-300 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
          />
        </div>

        <div className="md:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <label htmlFor="content" className="block text-sm font-medium text-foreground-800">
              详细经过 <span className="text-accent-600">*</span>
            </label>
            <span
              className={`text-xs ${contentLength > MAX_LENGTH ? "text-accent-700" : "text-foreground-500"}`}
            >
              {contentLength} / {MAX_LENGTH}
            </span>
          </div>
          <textarea
            id="content"
            name="content"
            required
            rows={7}
            maxLength={MAX_LENGTH}
            onChange={(event) => setContentLength(event.target.value.length)}
            placeholder="请描述发生了什么、大致时间和地点、你是如何了解的。不要填写你的姓名、工号或联系方式。"
            className="w-full px-4 py-3 rounded-md text-sm leading-relaxed bg-background-100 border border-background-300 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-y"
          ></textarea>
          <p className="mt-2 text-xs text-foreground-500">单次投稿上限 {MAX_LENGTH} 字，超出将无法提交。</p>
        </div>

        <div className="md:col-span-1">
          <label htmlFor="evidence" className="block text-sm font-medium text-foreground-800 mb-2">
            佐证情况（选填）
          </label>
          <select
            id="evidence"
            name="evidence"
            defaultValue=""
            className="w-full px-4 py-3 rounded-md text-sm bg-background-100 border border-background-300 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
          >
            <option value="" disabled>
              请选择最接近的情况
            </option>
            {evidenceOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="md:col-span-1">
          <label htmlFor="code" className="block text-sm font-medium text-foreground-800 mb-2">
            匿名回执代号（选填）
          </label>
          <input
            id="code"
            name="code"
            type="text"
            maxLength={32}
            placeholder="自定义一串字符，只有你自己知道"
            className="w-full px-4 py-3 rounded-md text-sm bg-background-100 border border-background-300 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
          />
        </div>

        <div className="form-field-asst" aria-hidden="true">
          <label htmlFor="company_alt">公司名称（请勿填写）</label>
          <input
            id="company_alt"
            name="company_alt"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            readOnly
          />
        </div>
      </div>

      {status !== "idle" && (
        <div
          role="status"
          className={`mt-6 flex items-start gap-3 rounded-md px-4 py-3.5 text-sm ${
            status === "success"
              ? "bg-primary-100 text-primary-800 border border-primary-200"
              : "bg-accent-100 text-accent-900 border border-accent-200"
          }`}
        >
          <i
            className={`${status === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} text-lg mt-0.5`}
            aria-hidden="true"
          ></i>
          <span className="leading-relaxed">{message}</span>
        </div>
      )}

      <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-4">
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md text-sm font-medium whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <i className="ri-send-plane-fill" aria-hidden="true"></i>
          {submitting ? "正在提交…" : "匿名提交"}
        </button>
        <p className="text-xs text-foreground-500 leading-relaxed">
          提交即表示你已阅读并理解
          <a href="#notes" className="text-primary-700 hover:text-primary-600 mx-1">
            投稿须知
          </a>
          ，请勿提交任何可识别你个人身份的材料。
        </p>
      </div>
    </form>
  );
}